import React, { forwardRef, useEffect, useState } from 'react';
import { Autocomplete, TextField, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Controller, useFormContext } from 'react-hook-form';
import { DynamicModuleUiModuleBody, SchemaCatalogResponse, SchemaFieldDefinition } from '../../../../../API';
import { UIModuleTypeForm } from '../../UIModuleForm';
import { FormBody } from '../../styles';
import { useSchemasAPI } from '../../../../../hooks/API/Config/useSchemasAPI';
import { useLocales, useTheme } from '../../../../../hooks';
import { DynamicField } from './DynamicField';

export const dynamicModuleFormTestIds = {
  formBody: 'dynamic-module-form.body',
  schemaSelector: 'dynamic-module-form.schema-selector',
  fieldsSection: 'dynamic-module-form.fields-section',
  fieldWrapper: (key: string): string => `dynamic-module-form.field.${key}`
};

const useStyles = makeStyles()((theme) => ({
  schemaSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4)
  },
  dynamicFields: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4)
  }
}));

const DynamicModuleForm = forwardRef<UIModuleTypeForm<DynamicModuleUiModuleBody>>(() => {
  const { classes } = useStyles();
  const { t } = useLocales();
  const { control, watch, setValue } = useFormContext<DynamicModuleUiModuleBody>();
  const schemasAPI = useSchemasAPI();
  const { formControlColor } = useTheme();
  const [schemas, setSchemas] = useState<SchemaCatalogResponse[]>([]);
  const [selectedSchema, setSelectedSchema] = useState<SchemaCatalogResponse | null>(null);

  const schemaId = watch('schemaId');

  useEffect(() => {
    schemasAPI
      .getAll()
      .then(({ data }) => setSchemas(data.body))
      .catch(() => setSchemas([]));
  }, []);

  useEffect(() => {
    const found = schemas.find((s) => s.id === schemaId) ?? null;
    setSelectedSchema(found);
  }, [schemaId, schemas]);

  const onSchemaChange = (schema: SchemaCatalogResponse | null) => {
    setValue('schemaId', schema?.id ?? '', { shouldValidate: true });
    setValue('data', {});
    setSelectedSchema(schema);
  };

  return (
    <FormBody data-testid={dynamicModuleFormTestIds.formBody}>
      <div className={classes.schemaSection}>
        <Typography variant="subtitle2">{t('layouts.dynamic_module.schema')}</Typography>
        <Controller
          control={control}
          name="schemaId"
          rules={{ required: t('general.field_is_required') }}
          render={({ fieldState: { error } }) => (
            <Autocomplete<SchemaCatalogResponse>
              options={schemas}
              color={formControlColor}
              getOptionLabel={(s) => s.name}
              value={selectedSchema}
              onChange={(_, schema) => onSchemaChange(schema)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('layouts.dynamic_module.select_schema')}
                  size="small"
                  required
                  color={formControlColor}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
              data-testid={dynamicModuleFormTestIds.schemaSelector}
            />
          )}
        />
      </div>

      {selectedSchema && (
        <div className={classes.dynamicFields} data-testid={dynamicModuleFormTestIds.fieldsSection}>
          <Typography variant="subtitle2">{t('layouts.dynamic_module.fields')}</Typography>
          {Object.entries(selectedSchema.fields).map(([key, fieldDef]) => {
            const def = fieldDef as SchemaFieldDefinition;
            return (
              <div key={key} data-testid={dynamicModuleFormTestIds.fieldWrapper(key)}>
                <DynamicField control={control} fieldKey={key} fieldDef={def} />
              </div>
            );
          })}
        </div>
      )}
    </FormBody>
  );
});

DynamicModuleForm.displayName = 'DynamicModuleForm';

export default DynamicModuleForm;
